import {release} from "../release";
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {AppContext} from './app-context';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {closeConfirmationModal, openConfirmationModal, sendQuery} from './common/utils';
import {CookieService} from 'ngx-cookie';
import * as Sentry from '@sentry/angular';
import {VisitContext} from './visit-details/visit-context';
import {IE3IsStiDownResponse, Visit} from '@portbase/bezoekschip-service-typescriptmodels';
import {environment} from '../environments/environment';
import newCargoScreenSettings from "./visit-details/new-cargo-screen-settings.json";
import moment from "moment-timezone";
import {SessionMonitoringService} from "@portbase/session-monitoring";
import {interval, Observable, Subscription} from "rxjs";
import {map} from "rxjs/operators";
import {BeamerService} from "./beamer/beamer.service";
import {EmergencyProcedureService} from "./admin/emergencyprocedure/emergencyprocedure.service";

declare var Beamer;

const STI_DOWN_POLL_INTERVAL_MS = 15 * 60_000;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
  private readonly sessionMonitoringService: SessionMonitoringService = inject(SessionMonitoringService);

  env = environment;
  s000Url = AppComponent.getS000Url();
  iamLogoutUrl = AppComponent.getIamLogoutUrl();
  appContext = AppContext;
  visitContext = VisitContext;
  private beamerFilter = new Set();
  useNewLookAndFeel: boolean;
  useOpenSearch: boolean;
  isEmergencyProcedureDisabled = EmergencyProcedureService.isEmergencyProcedureDisabled()

  @ViewChild("timoutModal")
  timoutModal: TemplateRef<any>;

  // Guards startStiDownPolling() so it is only ever started once, even though
  // routeBasedOnUserProfile (which sets AppContext.userProfile) re-invokes itself on a
  // recursive ~5 minute refresh timer for the lifetime of the session.
  private stiDownPollingStarted = false;
  // Holds the interval() subscription so a stale/expired session (401 on a poll tick) can
  // stop the polling loop instead of letting it run forever regardless of session state -
  // see pollStiDown()'s error handler for why this matters.
  private stiDownPollingSubscription?: Subscription;

  constructor(route: ActivatedRoute, private router: Router,
              private cookieService: CookieService,
              private beamer: BeamerService) {
    moment.tz.setDefault('Europe/Amsterdam');
    this.useNewLookAndFeel = AppContext.useNewLookAndFeel();
    AppComponent.startSentry();

    router.events.subscribe(e => {
      if (e instanceof NavigationEnd) {
        this.setApplicationContext(e);
        AppContext.clearAlerts();
        AppContext.setSupportPageForTask();

        if ((!AppContext.isSystemlink && !AppContext.userProfile) || (AppContext.isSystemlink && this.env.production)) {
          const jwt = router.parseUrl(e.url).queryParamMap.get('jwt');
          if (jwt) {
            cookieService.put("portvisitjwt", jwt);
            router.navigate([], {
              queryParams: {
                jwt: null
              },
              queryParamsHandling: 'merge'
            });
          } else {
            this.routeBasedOnUserProfile(router);
          }
        }
      }
    });
  }

  private setApplicationContext(e: NavigationEnd) {
    const url = e.url;
    AppContext.isSystemlink = url?.startsWith("/systemlink");

    if (url?.startsWith("/transit")) {
      this.beamer.addFilter("transitaangifte");
    } else if (url?.startsWith("/cargo") || url?.startsWith("/commercial-release") || url?.startsWith("/transhipment")) {
      this.beamer.addFilter("melding-lading-import");
    } else {
      this.beamer.addFilter("melding-schip");
    }
  }

  private static startSentry() {
    if (AppContext.environment) {
      Sentry.init({
        release: release,
        environment: AppContext.environment === 'pcs' ? 'prod' : AppContext.environment,
        dsn: environment.sentryUrl,
        integrations: [Sentry.captureConsoleIntegration({
          levels: [
            'error'
          ]
        })]
      });
    }
  }

  private routeBasedOnUserProfile = (router: Router, initialPageLoad = true, retryCount = 0) => {
    sendQuery('com.portbase.bezoekschip.common.api.authorisation.GetUserProfile', {},
      {caching: false, showSpinner: !AppContext.userProfile, hideError: !!AppContext.userProfile}).subscribe(profile => {
      AppContext.userProfile = profile;
      this.beamer.init(profile); // initialise Beamer only now that the user is actually known (idempotent)
      // STI-down banner audience: admins, customs and cargo import viewers/editors
      // (isCargoImportViewer() includes editors) - the users whose ICS2 cargo declarations are
      // affected when STI is down. Everyone else is never polled, so stiDownStatus stays null
      // and the banner stays hidden.
      if (AppContext.isAdminOrCustoms() || AppContext.isCargoImportViewer()) {
        this.startStiDownPolling(); // idempotent - do not poll before a user is actually logged in
      }
      const environmentSettings: { [key: string]: string[] } = newCargoScreenSettings[AppContext.environment] || {};
      if (environmentSettings[AppContext.userProfile?.organisation?.shortName] != null) {
        if (localStorage.getItem("useNewLookAndFeel") != "true") {
          localStorage.setItem("useNewLookAndFeel", "true");
          window.location.reload();
        }
      }
      const scope = Sentry.getCurrentScope();
      if (scope) {
        scope.setUser({
          username: AppContext.userProfile.userName,
          email: AppContext.userProfile.organisation?.emailAddress,
          contact: AppContext.userProfile.organisation?.contact,
          organisationShortName: AppContext.userProfile.organisation?.shortName
        });
      }
			if (initialPageLoad) {
        if (!AppContext.isAllowedToViewVisits()) {
          if (AppContext.isWasteCollector()) {
            AppContext.navigateWithHistory(router,'/waste');
          }
        }
        if (AppContext.isSystemlink) {
          AppContext.navigateWithHistory(router, "/");
        }
			}
      setTimeout(() => this.routeBasedOnUserProfile(router, false), 290_000); //refresh every ±5 minute
    }, error => {
      if (error.status === 401) {
        const headers = error.headers;
        this.redirectToLoginPage();
      } else if (retryCount < 9) {
        setTimeout(() => this.routeBasedOnUserProfile(router, true, retryCount + 1), 1_000);
      } else {
        alert("Portvisit appears to be unavailable. Please wait a few minutes and try again.");
      }
    });
  };

  // Starts polling the real IsStiDown query on an interval, once a user is actually logged
  // in (routeBasedOnUserProfile calls this after AppContext.userProfile is set). Idempotent:
  // routeBasedOnUserProfile re-invokes itself every ~5 minutes for the lifetime of the
  // session, so this guards against starting a second, overlapping interval().
  private startStiDownPolling(): void {
    if (this.stiDownPollingStarted) {
      return;
    }
    this.stiDownPollingStarted = true;
    this.pollStiDown();
    this.stiDownPollingSubscription = interval(STI_DOWN_POLL_INTERVAL_MS).subscribe(() => this.pollStiDown());
  }

  // Note: a 401 here means the server-side session has actually expired (e.g. the laptop was
  // closed/asleep overnight, then reopened - the interval() tick still fires because nothing
  // was watching session state, but the session itself is long gone). This is NOT a transient
  // background-poll failure: without stopping the loop, every subsequent tick keeps hitting
  // the backend with a dead session and keeps logging the same "poll failed" error forever,
  // which is also why this showed up repeatedly in Sentry (console.error is forwarded there via
  // captureConsoleIntegration, see startSentry() above) - a routine, expected condition was being
  // reported as a recurring application error. Stop the loop and route to the same login redirect
  // routeBasedOnUserProfile already uses for a 401 on its own, unrelated profile-refresh call.
  private pollStiDown(): void {
    sendQuery<IE3IsStiDownResponse>('com.portbase.bezoekschip.common.api.consignments.queries.IsStiDown', {},
      {caching: false, hideError: true}).subscribe({
      next: response => AppContext.stiDownStatus = response,
      error: error => {
        if (error.status === 401) {
          this.stiDownPollingSubscription?.unsubscribe();
          this.stiDownPollingStarted = false; // allow a future login to restart polling
          this.redirectToLoginPage();
          return;
        }
        // A failed background health-check poll must not crash the polling loop or surface a
        // user-facing error (mirrors this file's own non-critical-background-call convention:
        // {hideError: true} already suppresses the QueryGateway's default alert registration;
        // this also protects against the Observable erroring and silently killing future ticks).
        console.error('IsStiDown poll failed', error);
      }
    });
  }

  private redirectToLoginPage() {
    if (environment.production) {
      window.location.href = '/api/login';
    }
    else if(environment.docker){
      window.location.href = 'http://localhost:8085/api/login';
    }
  }

  private static getS000Url() {
    return 'https://' + AppContext.environment + '.portbase.com';
  }

  private static getIamLogoutUrl() {
    const iamEnvironment = AppContext.iamEnvironment;
    const credEnvironment = AppContext.credentialsEnvironment;
    return iamEnvironment && credEnvironment
      ? `https://login.${iamEnvironment}.eu/logout?lang=en&redirect_uri=https:%2F%2Flogout.${credEnvironment}.portbase.com%2Fen`
      : "#";
  }

  static getTimeOutUrl(): string {
    return `https://logout.${AppContext.credentialsEnvironment}.portbase.com/timeout/en/?redirect=${window.location.href}`;
  }

  get visit(): Visit {
    return this.visitContext.visit;
  }

  get cargoScreenEnabled(): boolean {
    const enabledForEnvironment = AppContext.isAdmin()
      || (AppContext.isCargoImportViewer() && AppContext.cargoDeclarantUsesNewCargoScreen(AppContext.userProfile.organisation.shortName))
      || AppContext.environment === "kt";
    return enabledForEnvironment && AppContext.useNewLookAndFeel();
  }

  get commercialReleaseNewScreenEnabled(): boolean {
    return AppContext.commercialReleaseNewScreenEnabled();
  }

  get commercialReleaseNewScreenPath(): string {
    return this.commercialReleaseNewScreenEnabled ? '/commercial-release-new' : '/commercial-release';
  }

  get timeoutCount(): Observable<string> {
    return this.sessionMonitoringService.onTimeoutWarning
      .pipe(map(countdown => this._formatCountdown(countdown)));
  }

  ngOnInit(): void {
    this.sessionMonitoringService.refresh().subscribe(
      () => this.sessionMonitoringService.watch());
    this.sessionMonitoringService.onIdleStart.subscribe(
      () => {
        $(window.document.body).addClass("session-monitoring-active");
        openConfirmationModal(this.timoutModal, undefined, 'static');
      });
    this.sessionMonitoringService.onTimeout.subscribe(
      () => this.timeout());
  }

  logOutAsServiceDesk = () => {
    if (environment.production) {
      window.location.href = '/api/logout';
    }
    else if(environment.docker){
      window.location.href = 'http://localhost:8085/api/logout';
    }
  }

  logOut() {
    this.sessionMonitoringService.stop();
    window.location.href = AppComponent.getIamLogoutUrl();
  }

  continueSession() {
    this.sessionMonitoringService.stop();
    this.sessionMonitoringService.refresh().subscribe(() => {
      this.sessionMonitoringService.watch();
      closeConfirmationModal();
      $(window.document.body).removeClass("session-monitoring-active");
    });
  }

  timeout() {
    window.location.assign(AppComponent.getTimeOutUrl());
  }

  private _formatCountdown(countdown: number): string {
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown - minutes * 60;
    return `${minutes > 0 ? `${minutes}m` : ``} ${seconds}s`;
  }

  changeUseOpenSearch = (useOpenSearch: boolean) => {
    this.useOpenSearch = useOpenSearch;
    if (useOpenSearch) {
      localStorage.setItem("useOpenSearch", "true");
    } else {
      localStorage.setItem("useOpenSearch", "false");
    }
  }
}
